import { useCallback } from 'react'

import {
  SATU_EVENT_TRACKER_EVENT_NAME,
  SATU_EVENT_DEFAULT_EVENT_TYPE,
} from 'utils/satu-event-tracker/config/event-tracking'
import { useSatuEventTracker } from './useSatuEventTracker'

interface ClickEventAttributes {
  element_name?: string
  element_type?: string
  screen_name?: string
  [key: string]: any
}

interface UseClickTrackerOptions {
  eventName?: string
  activityType: string
  defaultAttributes?: ClickEventAttributes
  enabled?: boolean
}

/**
 * Custom hook to track click events with SatuEvent tracker.
 * Returns a function that can be called inside onClick handlers.
 *
 * @example
 * ```tsx
 * const trackClick = useClickTracker({
 *   defaultAttributes: {
 *     screen_name: 'ide_praktik_video_inspirasi',
 *   },
 * })
 *
 * // Usage in component
 * <button onClick={() => trackClick({ element_name: 'share_button' })}>
 *   Share
 * </button>
 * ```
 */
export const useClickTracker = ({
  eventName = SATU_EVENT_DEFAULT_EVENT_TYPE,
  activityType,
  defaultAttributes = {},
  enabled = true,
}: UseClickTrackerOptions) => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()

  const trackClick = useCallback(
    (
      attributes: ClickEventAttributes = {},
      options?: Partial<{
        /**
         * If true, attempts to send the tracker event using the Beacon API.
         */
        keepAlive: boolean
      }>
    ) => {
      if (!enabled) return
      SETrackEvent({
        eventName: SATU_EVENT_TRACKER_EVENT_NAME,
        eventParams: {
          activity_type: activityType,
          event_name: eventName,
          attributes: SETransformAttrs({
            ...defaultAttributes,
            ...attributes,
          }),
        },
        keepAlive: options?.keepAlive,
      })
    },
    [
      SETrackEvent,
      SETransformAttrs,
      eventName,
      activityType,
      defaultAttributes,
      enabled,
    ]
  )

  return trackClick
}
